import React, { useState } from 'react';
import axios from 'axios';
import '../styles/pages/Contact.scss';
import { API_BASE_URL } from '../utils/apiConfig';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    reason: '',
    subject: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [dynamicContactInfo, setDynamicContactInfo] = useState(null);
  const [testimonials, setTestimonials] = useState([]);

  React.useEffect(() => {
    // Fetch Contact Info
    axios.get(`${API_BASE_URL}/cms/contact-info`)
      .then(res => {
        if (res.data.success && res.data.data) {
          setDynamicContactInfo(res.data.data);
        }
      })
      .catch(err => console.error("Error fetching contact info:", err));

    // Fetch Testimonials
    axios.get(`${API_BASE_URL}/cms/testimonials`)
      .then(res => {
        if (res.data.success && res.data.data) {
          setTestimonials(res.data.data);
        }
      })
      .catch(err => console.error("Error fetching testimonials:", err));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Create contact submission with proper schema
      const contactSubmission = {
        name: formData.name,
        email: formData.email,
        reason: formData.reason || 'General Inquiry',
        subject: formData.subject,
        message: formData.message
      };

      const response = await axios.post(`${API_BASE_URL}/contact`, contactSubmission);

      if (response.status === 200 || response.data?.success) {
        setSubmitted(true);
        setFormData({ name: '', email: '', reason: '', subject: '', message: '' });
        setTimeout(() => setSubmitted(false), 5000);
      }
    } catch (err) {
      console.error('Form submission error:', err);
      if (err.code === 'ECONNABORTED' || err.code === 'ECONNREFUSED') {
        setError('⚠️ Backend server not running. Please try again later.');
      } else if (err.response?.status === 422) {
        setError('❌ Please fill in all required fields correctly.');
      } else if (err.message === 'Network Error') {
        setError('⚠️ Backend server not running. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const contactInfoList = [
    {
      icon: '📍',
      title: 'Address',
      details: dynamicContactInfo?.address || 'Office No. 1 & 2, Pune - Nashik Hwy, opposite to Amrutvahini Engineering College, Amrutnagar, Ghulewadi, Maharashtra 422608, India'
    },
    {
      icon: '📞',
      title: 'Phone',
      details: dynamicContactInfo?.phone || '+91 9762424851',
      link: `tel:${dynamicContactInfo?.phone || '+919762424851'}`
    },
    {
      icon: '📧',
      title: 'Email',
      details: dynamicContactInfo?.email || 'contact@pravarainfotech.in',
      link: `mailto:${dynamicContactInfo?.email || 'contact@pravarainfotech.in'}`
    },
    {
      icon: '🕐',
      title: 'Hours',
      details: 'Mon - Sat: 10:00 AM - 6:00 PM'
    }
  ];

  return (
    <div className="contact">
      {/* Hero Section */}
      <section className="contact-hero" style={{ backgroundImage: "linear-gradient(135deg, rgba(10, 37, 64, 0.85), rgba(0, 82, 106, 0.85)), url('/images/cta-bg.jpg')" }}>
        <h1>Contact Us</h1>
        <p>Get in touch for project inquiries, support, or partnerships</p>
      </section>

      {/* Contact Section */}
      <section className="contact-section">
        <div className="contact-grid">
          {/* Contact Info */}
          <div className="contact-info">
            <h3>Get in Touch</h3>
            <p>Have a project in mind? Let's discuss how we can help.</p>

            {contactInfoList.map((item, idx) => (
              <div key={idx} className="ci-item">
                <div className="ci-icon">{item.icon}</div>
                <div>
                  <h4>{item.title}</h4>
                  {item.link ? (
                    <p><a href={item.link}>{item.details}</a></p>
                  ) : (
                    <p>{item.details}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Contact Form */}
          <div className="contact-form">
            <h3>Send Us a Message</h3>
            <p>Fill out the form below and we'll get back to you.</p>

            {submitted && <p className="success-message">Thank you! We'll get back to you soon.</p>}
            {error && <p className="error-message">{error}</p>}

            <form onSubmit={handleSubmit} className="form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Name <span>*</span></label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    placeholder="Your full name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email <span>*</span></label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Your email address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="reason">Reason for Contact</label>
                <select
                  id="reason"
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                >
                  <option value="">Select a reason</option>
                  <option value="Project Inquiry">Project Inquiry</option>
                  <option value="Support">Support</option>
                  <option value="Partnership">Partnership</option>
                  <option value="Feedback">Feedback</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="subject">Subject</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  placeholder="Subject of your message"
                  value={formData.subject}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Message <span>*</span></label>
                <textarea
                  id="message"
                  name="message"
                  rows="5"
                  placeholder="Write your message here..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                ></textarea>
              </div>

              <button type="submit" className="btn-send" disabled={loading}>
                {loading ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="map-section">
        <div className="map-container">
          <h2>Our Office Location</h2>
          <p className="map-address">📍 Office No. 1 & 2, Pune - Nashik Hwy, opposite to Amrutvahini Engineering College, Amrutnagar, Ghulewadi, Maharashtra 422608, India</p>
          <div className="map-wrapper">
            <iframe
              title="Pravara InfoTech Location Map"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3773.8547098426347!2d74.1846325!3d19.609966!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bdd01252e612c17:0xe4f600c1e78701b5!2sPravara%20Infotech!5e0!3m2!1sen!2sin!4v1703695200000"
              width="100%"
              height="500"
              style={{ border: 0, borderRadius: '12px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
          <a href="https://www.google.com/maps/place/Pravara+Infotech,+Office+No.+1+%26+2,+Pune+-+Nashik+Hwy,+opposite+to+Amrutvahini+Engineering+College,+Amrutnagar,+Ghulewadi,+Maharashtra+422608/@19.609966,74.1846325,15z" target="_blank" rel="noopener noreferrer" className="open-maps-link">
            📍 Open in Google Maps →
          </a>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <h2>What People Say</h2>
        <p className="section-desc">Client Feedback</p>
        <div className="testimonials-grid">
          {testimonials.map((testimonial) => (
            <div key={testimonial.id} className="testimonial-card">
              <p className="testimonial-text">"{testimonial.quote || testimonial.message}"</p>
              <span className="testimonial-author">— {testimonial.author}</span>
            </div>
          ))}
          {testimonials.length === 0 && (
            <div className="testimonial-card">
              <p className="testimonial-text">"The Pravara team was incredibly responsive and helpful. They made the process seamless!"</p>
              <span className="testimonial-author">— Alex R.</span>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="contact-cta">
        <h2>Ready to Start Your Project?</h2>
        <p>Let's collaborate and bring your ideas to life. Contact us today for a free consultation.</p>
        <button className="btn-cta" onClick={() => document.querySelector('.contact-form').scrollIntoView({ behavior: 'smooth' })}>
          Start Your Project →
        </button>
      </section>
    </div>
  );
}

export default Contact;
